<template>
  <div class="home-container">
    <!-- 动态粒子背景 -->
    <canvas ref="particleCanvas" class="particle-canvas"></canvas>
    <!-- 顶部导航 -->
    <div class="nav-bar">
      <div class="nav-content">
        <div class="logo">
          <el-icon class="logo-icon"><ShoppingBag /></el-icon>
          <span class="glow-text">云商城</span>
        </div>
        <div class="nav-menu">
          <el-menu mode="horizontal" default-active="1" class="nav-menu-inner">
            <el-menu-item index="1" @click="goToHome">首页</el-menu-item>
            <el-menu-item index="2">
              <el-dropdown @command="handleCategorySelect" trigger="click" placement="bottom-start">
                <span class="el-dropdown-link">
                  商品分类
                  <el-icon class="el-icon--right"><arrow-down /></el-icon>
                </span>
                <template #dropdown>
                  <el-dropdown-menu class="category-dropdown">
                    <el-dropdown-item command="all" :class="{ 'selected': selectedNavCategory === 'all' }">
                      <span>全部分类</span>
                    </el-dropdown-item>
                    <el-dropdown-item 
                      v-for="category in navCategories" 
                      :key="category.id"
                      :command="category.value"
                      :class="{ 'selected': selectedNavCategory === category.value }"
                    >
                      <span>{{ category.name }}</span>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </el-menu-item>
            <el-menu-item index="3" @click="goToOrders">我的订单</el-menu-item>
            <el-menu-item index="4" @click="goToCart">购物车</el-menu-item>
            <el-menu-item index="5">
              <el-dropdown trigger="click" placement="bottom-start" @command="handleCouponCommand">
                <span class="el-dropdown-link">
                  <el-icon style="margin-right:3px;"><Ticket /></el-icon>
                  我的优惠券
                  <el-tag v-if="couponList.filter(c=>!c.claimed&&!c.expired).length" size="small" type="danger" round style="margin-left:4px;transform:scale(0.85);">{{ couponList.filter(c=>!c.claimed&&!c.expired).length }}</el-tag>
                  <el-icon class="el-icon--right"><arrow-down /></el-icon>
                </span>
                <template #dropdown>
                  <el-dropdown-menu class="coupon-dropdown-menu">
                    <el-dropdown-item v-for="c in couponList" :key="c.id" :command="c.id" :disabled="c.expired">
                      <div class="coupon-dropdown-item" :class="{ expired: c.expired, claimed: c.claimed }">
                        <div class="coupon-dd-left" :style="{ background: c.expired ? '#ccc' : c.claimed ? '#43b581' : c.color }">
                          <span class="coupon-dd-val">{{ c.label }}</span>
                        </div>
                        <div class="coupon-dd-right">
                          <span class="coupon-dd-name">{{ c.name }}</span>
                          <span class="coupon-dd-desc">{{ c.desc }}</span>
                          <span class="coupon-dd-expire">{{ c.expired ? '已过期' : c.expireText }}</span>
                        </div>
                        <div class="coupon-dd-action">
                          <span v-if="c.expired" class="dd-tag expired-tag">已过期</span>
                          <span v-else-if="c.claimed" class="dd-tag claimed-tag">已领取</span>
                          <span v-else class="dd-tag claim-tag">领取</span>
                        </div>
                      </div>
                    </el-dropdown-item>
                    <div class="coupon-dd-footer">更多优惠活动敬请期待~</div>
                  </el-dropdown-menu>
                </template>
              </el-dropdown>
            </el-menu-item>
          </el-menu>
        </div>
        <div class="search-bar">
          <el-input
            v-model="searchKeyword"
            placeholder="搜索商品名称"
            class="search-input"
            clearable
            @keyup.enter="handleSearch"
            @clear="handleSearchClear"
          >
            <template #suffix>
              <el-icon class="search-icon" @click="handleSearch"><Search /></el-icon>
            </template>
          </el-input>
        </div>
        <div class="user-info">
          <el-dropdown @command="handleCommand">
            <span class="el-dropdown-link">
              {{ userNickname || '用户' }}
              <el-icon class="el-icon--right"><arrow-down /></el-icon>
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item command="profile">个人资料</el-dropdown-item>
                <el-dropdown-item command="settings">账号设置</el-dropdown-item>
                <el-dropdown-item divided command="logout">退出登录</el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>
        </div>
      </div>
    </div>

    <!-- 主内容区 -->
    <div class="main-content" style="padding-top: 36px;">
      <!-- 商品分类卡片 -->
      <div class="category-section" style="margin-top: 0; margin-bottom: 18px;">
        <h2 style="margin-bottom: 8px; font-size: 1.15rem; color: rgba(255,255,255,0.9);">热门分类</h2>
        <el-row :gutter="12" style="row-gap: 14px;">
          <el-col :xs="12" :sm="8" :md="6" v-for="category in categories" :key="category.id">
            <div 
              class="category-card" 
              @click="selectCategory(category)"
              :style="currentCategory === category.value ? { borderColor: category.color, boxShadow: `0 0 16px ${category.color}40` } : {}"
            >
              <div class="category-card-bg" :style="{ background: getCategoryBgGradient(category.value) }"></div>
              <div class="category-icon" style="position:relative; z-index:2;">
                <el-icon size="32" :color="category.color" style="filter:none;">
                  <component :is="category.icon" />
                </el-icon>
              </div>
              <div class="category-name" :style="{ color: category.color, fontWeight: 'bold', position: 'relative', zIndex: 2 }">{{ category.name }}</div>
              <div class="category-count" style="color: rgba(255,255,255,0.5); position:relative; z-index:2;">点击查看商品</div>
            </div>
          </el-col>
        </el-row>
      </div>
      <!-- 分类精选展示区域 -->
      <transition name="fade-slide">
        <div v-if="showFeatured && featuredList.length" class="featured-section"
          :style="{
            background: `linear-gradient(135deg, rgba(${currentFeatured.rgba},0.06) 0%, rgba(${currentFeatured.rgba},0.10) 100%)`,
            borderColor: `rgba(${currentFeatured.rgba},0.25)`
          }">
          <div class="featured-header">
            <div class="featured-title">
              <span class="featured-icon">{{ currentFeatured.icon }}</span>
              <h2 :style="{ background: `linear-gradient(135deg, ${currentFeatured.color1}, ${currentFeatured.color3})`, '-webkit-background-clip': 'text', '-webkit-text-fill-color': 'transparent', 'background-clip': 'text' }">{{ currentFeatured.name }}</h2>
              <span class="featured-icon">{{ currentFeatured.icon }}</span>
            </div>
            <div class="featured-actions">
              <button class="featured-refresh"
                :style="{ borderColor: `rgba(${currentFeatured.rgba},0.4)`, color: currentFeatured.color1 }"
                @click="selectCategory({ value: featuredType })">
                <el-icon><Loading /></el-icon> 换一批
              </button>
              <button class="featured-close" @click="showFeatured = false">
                <el-icon><Close /></el-icon> 显示全部
              </button>
            </div>
          </div>
          <el-row :gutter="16" style="row-gap: 18px;">
            <el-col :xs="24" :sm="12" :md="6" :lg="6" v-for="product in featuredList" :key="'feat-' + product.id">
              <div class="product-card-wrapper glassmorphism featured-card"
                :style="{
                  borderColor: `rgba(${currentFeatured.rgba},0.5)`,
                  boxShadow: `0 4px 20px rgba(${currentFeatured.rgba},0.15), 0 0 0 1px rgba(${currentFeatured.rgba},0.1)`
                }">
                <div class="featured-badge" :style="{ background: `linear-gradient(135deg, ${currentFeatured.color1}, ${currentFeatured.color2})` }">{{ currentFeatured.badge }}</div>
                <div class="product-image" style="height: 120px; border-radius: 14px 14px 0 0; cursor: pointer;" @click="goToProductDetail(product)">
                  <el-image
                    :src="getProductImage(product.picUrl, product)"
                    fit="cover"
                    :preview-src-list="[getProductImage(product.picUrl, product)]"
                    class="product-img"
                    style="height: 120px; min-height: 120px; border-radius: 14px 14px 0 0; pointer-events: none;"
                  >
                    <template #error>
                      <div class="image-placeholder">
                        <el-icon size="32" color="#ccc"><Goods /></el-icon>
                        <span>暂无图片</span>
                      </div>
                    </template>
                  </el-image>
                </div>
                <div class="product-info" style="padding: 10px 8px; gap: 6px;">
                  <h3 class="product-title" style="font-size: 0.98rem;">{{ product.title || product.name }}</h3>
                  <div class="product-meta" style="gap: 6px;">
                    <span class="price" style="font-size: 1.05rem;">¥{{ product.price }}</span>
                    <span class="stock" :class="{ 'low-stock': product.stock < 10 }" style="font-size: 0.8rem;">库存: {{ product.stock }}</span>
                  </div>
                  <div class="product-category" v-if="product.category">
                    <el-tag size="small">{{ product.category }}</el-tag>
                  </div>
                  <button class="btn-add-cart featured-cart-btn" @click="handleAddToCart(product)" style="padding: 6px 0; font-size: 0.9rem;"
                    :style="{ background: `linear-gradient(135deg, ${currentFeatured.color1} 0%, ${currentFeatured.color3} 100%)` }">
                    <el-icon><shopping-cart-icon /></el-icon>
                    加入购物车
                  </button>
                </div>
              </div>
            </el-col>
          </el-row>
        </div>
      </transition>

      <!-- 全部商品区域 -->
      <div v-show="!showFeatured" class="products-section" style="margin-top: 0;">
        <div class="section-header" style="margin-bottom: 10px;">
          <h2 style="margin-bottom: 4px; font-size: 1.1rem; color: rgba(255,255,255,0.9);">全部商品</h2>
          <span class="total-count">共 {{ totalProducts }} 件商品</span>
        </div>
        <!-- 商品网格 - 无限滚动容器 -->
        <div 
          v-infinite-scroll="loadMore" 
          :infinite-scroll-disabled="loadingMore || !hasMore"
          :infinite-scroll-delay="500"
          :infinite-scroll-distance="100"
          class="infinite-scroll-container"
        >
          <el-row :gutter="16" style="row-gap: 18px;" v-loading="loading && isFirstLoad" element-loading-text="加载中...">
            <el-col :xs="24" :sm="12" :md="6" :lg="6" v-for="product in productList" :key="`${product.id}-${productList.indexOf(product)}`">
              <div class="product-card-wrapper glassmorphism" style="border-radius:14px;">
                <!-- 商品图片 -->
                <div class="product-image" style="height: 120px; border-radius: 14px 14px 0 0; cursor: pointer;" @click="goToProductDetail(product)">
                  <el-image 
                    :src="getProductImage(product.picUrl, product)" 
                    fit="cover"
                    :preview-src-list="[getProductImage(product.picUrl, product)]"
                    class="product-img"
                    style="height: 120px; min-height: 120px; border-radius: 14px 14px 0 0; pointer-events: none;"
                  >
                    <template #error>
                      <div class="image-placeholder">
                        <el-icon size="32" color="#ccc"><Goods /></el-icon>
                        <span>暂无图片</span>
                      </div>
                    </template>
                  </el-image>
                  <div class="product-badge" v-if="product.stock < 10">
                    <span>库存紧张</span>
                  </div>
                </div>
                <!-- 商品信息 -->
                <div class="product-info" style="padding: 10px 8px; gap: 6px;">
                  <h3 class="product-title" style="font-size: 0.98rem;">{{ product.title || product.name }}</h3>
                  <div class="product-meta" style="gap: 6px;">
                    <span class="price" style="font-size: 1.05rem;">¥{{ product.price }}</span>
                    <span class="stock" :class="{ 'low-stock': product.stock < 10 }" style="font-size: 0.8rem;">
                      库存: {{ product.stock }}
                    </span>
                  </div>
                  <div class="product-category" v-if="product.category">
                    <el-tag size="small">{{ product.category }}</el-tag>
                  </div>
                  <button class="btn-add-cart" @click="handleAddToCart(product)" style="padding: 6px 0; font-size: 0.9rem;">
                    <el-icon><shopping-cart-icon /></el-icon>
                    加入购物车
                  </button>
                </div>
              </div>
            </el-col>
            <!-- 空状态 -->
            <el-col v-if="!loading && productList.length === 0" :span="24">
              <div class="empty-state">
                <el-icon size="64" color="#ccc"><goods /></el-icon>
                <p>暂无商品数据</p>
              </div>
            </el-col>
          </el-row>

          <!-- 加载更多状态 -->
          <div v-if="loadingMore && hasMore" class="loading-more">
            <el-icon class="loading-icon"><Loading /></el-icon>
            <span>正在为您寻找好物...</span>
          </div>

          <!-- 没有更多数据 -->
          <div v-if="!hasMore && productList.length > 0" class="no-more-data">
            <span>已经到底啦~</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部 -->
    <div class="footer">
      <div class="footer-content">
        <div class="footer-section">
          <h4>关于我们</h4>
          <p>云商城是一个基于微服务架构的现代化交易平台。</p>
        </div>
        <div class="footer-section">
          <h4>快速链接</h4>
          <ul>
            <li><a href="#">帮助中心</a></li>
            <li><a href="#">联系我们</a></li>
            <li><a href="#">服务条款</a></li>
          </ul>
        </div>
        <div class="footer-section">
          <h4>联系方式</h4>
          <p>Email: support@cloudshop.com</p>
          <p>Phone: 400-800-8888</p>
        </div>
      </div>
      <div class="footer-bottom">
        <p>&copy; 2026 云商城 - 微服务交易平台. All rights reserved.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, computed, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import { ElNotification, ElMessage } from 'element-plus'
import { ShoppingBag, Cpu, ShoppingCart, Goods, ShoppingCart as ShoppingCartIcon, ArrowDown, Loading, Ticket, Close, Search } from '@element-plus/icons-vue'
import { productService, default as productAPI } from '../api/product'
import { cartService, getCurrentUserId } from '../api/order'

const router = useRouter()
const userNickname = ref('')
const particleCanvas = ref(null)
let particleAnimId = null
const currentCategory = ref(null)
const loading = ref(false)
const loadingMore = ref(false)
const productList = ref([])
const searchKeyword = ref('')
const allProducts = ref([]) // 保存所有商品用于搜索
const defaultImage = `data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="300" height="300" viewBox="0 0 300 300"><rect fill="%23f0f2f5" width="300" height="300"/><text x="150" y="145" text-anchor="middle" fill="%23bbb" font-size="16" font-family="sans-serif">暂无图片</text><path d="M130 170h40l-8-12-7 9-5-4z" fill="%23ccc"/><circle cx="140" cy="155" r="4" fill="%23ccc"/></svg>')}`

// 无限滚动参数
const currentPage = ref(1)
const pageSize = ref(12) // 每次加载12个商品
const totalProducts = ref(0)
const hasMore = ref(true)
const isFirstLoad = ref(true)

// 分类精选展示
const showFeatured = ref(false)
const featuredList = ref([])
const featuredType = ref('')  // 'new' | 'hot' | 'discount' | 'recommend'

// 导航栏分类数据
const navCategories = ref([])
const selectedNavCategory = ref('all')

// 热门分类卡片数据
const categories = ref([
  { id: 1, name: '新品上市', value: 'new', icon: 'Goods', color: '#a3a3ff', count: 0 },
  { id: 2, name: '热销商品', value: 'hot', icon: 'ShoppingBag', color: '#ff7f7f', count: 0 },
  { id: 3, name: '限时折扣', value: 'discount', icon: 'Cpu', color: '#2e8b57', count: 0 },
  { id: 4, name: '推荐好物', value: 'recommend', icon: 'ShoppingCart', color: '#ffb347', count: 0 },
])

const parseProductResponse = (responseData) => {
  const result = {
    list: [],
    total: 0,
    success: false,
    message: ''
  }

  if (Array.isArray(responseData)) {
    result.list = responseData
    result.total = responseData.length
    result.success = true
    return result
  }

  if (!responseData || typeof responseData !== 'object') {
    return result
  }

  if (Array.isArray(responseData.records)) {
    result.list = responseData.records
    result.total = Number(responseData.total) || responseData.records.length
    result.success = true
    return result
  }

  if (responseData.data) {
    const data = responseData.data
    if (Array.isArray(data)) {
      result.list = data
    } else if (Array.isArray(data.records)) {
      result.list = data.records
    } else if (Array.isArray(data.list)) {
      result.list = data.list
    }
    result.total = Number(responseData.total) || Number(data.total) || result.list.length
    result.success = true
    return result
  }

  if (Array.isArray(responseData.list)) {
    result.list = responseData.list
    result.total = Number(responseData.total) || responseData.list.length
    result.success = true
    return result
  }

  result.success = responseData.success !== false
  result.message = responseData.message || ''
  return result
}

/**
 * 加载更多商品（无限滚动）
 */
const loadMore = async () => {
  if (loadingMore.value || !hasMore.value) return
  
  loadingMore.value = true
  try {
    // 增加页码
    currentPage.value++
    
    const params = {
      page: currentPage.value,
      size: pageSize.value,
    }

    // 处理分类参数
    if (currentCategory.value && currentCategory.value !== 'all') {
      // 将分类值转换为中文分类名传给后端
      const categoryMap = {
        'phone': '手机数码',
        'computer': '电脑办公',
        'appliance': '家用电器',
        'clothing': '服装鞋帽',
        'beauty': '美妆个护',
        'food': '食品饮料',
        'sports': '运动户外',
        'books': '图书音像',
        'new': '新品上市',
        'hot': '热销商品',
        'discount': '限时折扣',
        'recommend': '推荐好物'
      }
      
      let categoryParam = currentCategory.value
      if (categoryMap[currentCategory.value]) {
        categoryParam = categoryMap[currentCategory.value]
      } else if (currentCategory.value.includes('_')) {
        const possibleChinese = currentCategory.value.replace(/_/g, '')
        if (categoryMap[possibleChinese]) {
          categoryParam = categoryMap[possibleChinese]
        }
      }
      
      params.category = categoryParam
    }
    
    const response = await productService.getProductList(params)
    const responseData = response.data
    const parsed = parseProductResponse(responseData)
    const newData = parsed.list
    const isSuccess = parsed.success

    if (isSuccess) {
      if (newData.length > 0) {
        // 追加新数据到现有列表
        productList.value.push(...newData)
        totalProducts.value = parsed.total || (productList.value.length)
        hasMore.value = productList.value.length < totalProducts.value
      } else {
        // 没有更多数据
        hasMore.value = false
      }
    } else {
      ElMessage.error(responseData.message || '获取商品列表失败')
      hasMore.value = false
    }
  } catch (error) {
    console.error('加载更多商品异常:', error)
    const errMsg = error.response?.data?.message || error.message || '网络连接失败'
    ElMessage.error(errMsg)
    hasMore.value = false
  } finally {
    loadingMore.value = false
  }
}

/**
 * 获取商品列表（支持分页和分类）- 初始加载
 */
const fetchProductList = async () => {
  currentPage.value = 1
  loading.value = true
  isFirstLoad.value = true
  try {
    const params = {
      page: currentPage.value,
      size: pageSize.value,
    }

    // 处理分类参数
    if (currentCategory.value && currentCategory.value !== 'all') {
      // 将分类值转换为中文分类名传给后端
      const categoryMap = {
        'phone': '手机数码',
        'computer': '电脑办公',
        'appliance': '家用电器',
        'clothing': '服装鞋帽',
        'beauty': '美妆个护',
        'food': '食品饮料',
        'sports': '运动户外',
        'books': '图书音像',
        'new': '新品上市',
        'hot': '热销商品',
        'discount': '限时折扣',
        'recommend': '推荐好物'
      }
      
      let categoryParam = currentCategory.value
      if (categoryMap[currentCategory.value]) {
        categoryParam = categoryMap[currentCategory.value]
      } else if (currentCategory.value.includes('_')) {
        const possibleChinese = currentCategory.value.replace(/_/g, '')
        if (categoryMap[possibleChinese]) {
          categoryParam = categoryMap[possibleChinese]
        }
      }
      
      params.category = categoryParam
      console.log('添加分类筛选参数:', params.category)
    }
    
    console.log('请求商品列表参数:', params)
    const response = await productService.getProductList(params)
    const responseData = response.data

    console.log('商品列表响应数据:', responseData)

    const parsed = parseProductResponse(responseData)
    let finalData = parsed.list
    let totalCount = parsed.total
    const isSuccess = parsed.success

    if (isSuccess && finalData.length === 0 && totalCount > 0) {
      const retryResponse = await productService.getProductList({ page: 1, size: pageSize.value })
      const retryParsed = parseProductResponse(retryResponse.data)
      finalData = retryParsed.list
      totalCount = retryParsed.total
    }

    if (isSuccess) {
      // 移除前端分类筛选，完全依赖后端筛选
      // 如果后端筛选不可用，需要确保后端接口支持category参数
      
      productList.value = finalData
      allProducts.value = [...finalData] // 保存所有商品用于搜索
      totalProducts.value = totalCount || finalData.length
      hasMore.value = productList.value.length < totalProducts.value
      
      // 调试信息
      console.log('最终商品列表:', productList.value)
      console.log('商品总数:', totalProducts.value)
    } else {
      console.error('获取商品列表失败:', responseData)
      ElMessage.error(responseData.message || '获取商品列表失败')
      hasMore.value = false
    }
  } catch (error) {
    console.error('获取商品列表异常:', error)
    console.error('错误详情:', error.response?.data || error.message)
    const errMsg = error.response?.data?.message || error.message || '网络连接失败'
    ElMessage.error(errMsg)
    hasMore.value = false
  } finally {
    loading.value = false
    isFirstLoad.value = false
  }
}

/**
 * 选择分类
 */
// 分类精选配置
const featuredConfig = {
  'recommend': { name: '推荐好物', icon: '✨', badge: '精选推荐', color1: '#d4a017', color2: '#ffd700', color3: '#ffb347', rgba: '255,215,0' },
  'new':       { name: '新品上市', icon: '🆕', badge: '新品首发', color1: '#6c63ff', color2: '#a3a3ff', color3: '#8b82ff', rgba: '163,163,255' },
  'hot':       { name: '热销商品', icon: '🔥', badge: '火爆热卖', color1: '#e74c3c', color2: '#ff7f7f', color3: '#ff6b6b', rgba: '255,127,127' },
  'discount':  { name: '限时折扣', icon: '💰', badge: '限时特惠', color1: '#1e7a46', color2: '#2e8b57', color3: '#43b581', rgba: '46,139,87' },
}
const currentFeatured = computed(() => featuredConfig[featuredType.value] || featuredConfig['recommend'])

const selectCategory = (category) => {
  const val = category.value
  if (['recommend', 'new', 'hot', 'discount'].includes(val)) {
    featuredType.value = val
    showFeatured.value = true
    const pool = [...productList.value]
    const count = Math.min(8, pool.length)
    const picked = []
    while (picked.length < count && pool.length > 0) {
      const idx = Math.floor(Math.random() * pool.length)
      picked.push(pool.splice(idx, 1)[0])
    }
    featuredList.value = picked
    if (picked.length === 0) {
      ElMessage.info('暂无商品可展示，请先浏览商品列表')
    }
    return
  }
  showFeatured.value = false
  currentCategory.value = category.value
  currentPage.value = 1
  fetchProductList()
}

/**
 * 获取分类背景渐变
 */
const getCategoryBgGradient = (categoryValue) => {
  const gradients = {
    'new': 'linear-gradient(135deg, #a3a3ff20 0%, #a3a3ff10 100%)',
    'hot': 'linear-gradient(135deg, #ff7f7f20 0%, #ff7f7f10 100%)',
    'discount': 'linear-gradient(135deg, #2e8b5720 0%, #2e8b5710 100%)',
    'recommend': 'linear-gradient(135deg, #ffb34720 0%, #ffb34710 100%)'
  }
  return gradients[categoryValue] || 'linear-gradient(135deg, #f0f0f0 0%, #f5f5f5 100%)'
}

/**
 * 根据商品名称/分类匹配合适的商品图片
 */
const productImageMap = {
  // 手机
  'iphone': 'https://cdn-icons-png.flaticon.com/512/0/191.png',
  'mate': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  'galaxy': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  'pixel': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  'xiaomi': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  'oppo': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  'vivo': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  'redmi': 'https://cdn-icons-png.flaticon.com/512/186/186239.png',
  '手机': 'https://cdn-icons-png.flaticon.com/512/0/191.png',
  // 电脑/笔记本
  'macbook': 'https://cdn-icons-png.flaticon.com/512/2004/2004580.png',
  'thinkpad': 'https://cdn-icons-png.flaticon.com/512/2004/2004580.png',
  '笔记本': 'https://cdn-icons-png.flaticon.com/512/2004/2004580.png',
  '电脑': 'https://cdn-icons-png.flaticon.com/512/2004/2004580.png',
  'ipad': 'https://cdn-icons-png.flaticon.com/512/2329/2329087.png',
  '平板': 'https://cdn-icons-png.flaticon.com/512/2329/2329087.png',
  // 耳机/音频
  'airpods': 'https://cdn-icons-png.flaticon.com/512/2652/2652218.png',
  '耳机': 'https://cdn-icons-png.flaticon.com/512/2652/2652218.png',
  '音箱': 'https://cdn-icons-png.flaticon.com/512/3659/3659784.png',
  '音响': 'https://cdn-icons-png.flaticon.com/512/3659/3659784.png',
  // 手表/穿戴
  '手表': 'https://cdn-icons-png.flaticon.com/512/2972/2972531.png',
  'watch': 'https://cdn-icons-png.flaticon.com/512/2972/2972531.png',
  '手环': 'https://cdn-icons-png.flaticon.com/512/2972/2972531.png',
  // 相机
  '相机': 'https://cdn-icons-png.flaticon.com/512/2956/2956744.png',
  '摄像': 'https://cdn-icons-png.flaticon.com/512/2956/2956744.png',
  // 家电
  '冰箱': 'https://cdn-icons-png.flaticon.com/512/3062/3062634.png',
  '洗衣机': 'https://cdn-icons-png.flaticon.com/512/3062/3062621.png',
  '空调': 'https://cdn-icons-png.flaticon.com/512/3062/3062626.png',
  '电视': 'https://cdn-icons-png.flaticon.com/512/2847/2847039.png',
  '微波炉': 'https://cdn-icons-png.flaticon.com/512/3062/3062649.png',
  // 服装
  '衣': 'https://cdn-icons-png.flaticon.com/512/2503/2503380.png',
  '裤': 'https://cdn-icons-png.flaticon.com/512/2503/2503387.png',
  '鞋': 'https://cdn-icons-png.flaticon.com/512/2742/2742528.png',
  '帽': 'https://cdn-icons-png.flaticon.com/512/2503/2503266.png',
  '服装': 'https://cdn-icons-png.flaticon.com/512/2503/2503380.png',
  // 美妆
  '口红': 'https://cdn-icons-png.flaticon.com/512/1940/1940922.png',
  '面膜': 'https://cdn-icons-png.flaticon.com/512/2553/2553695.png',
  '护肤': 'https://cdn-icons-png.flaticon.com/512/2553/2553695.png',
  '香水': 'https://cdn-icons-png.flaticon.com/512/2553/2553642.png',
  '美妆': 'https://cdn-icons-png.flaticon.com/512/1940/1940922.png',
  // 食品
  '零食': 'https://cdn-icons-png.flaticon.com/512/2515/2515183.png',
  '饮料': 'https://cdn-icons-png.flaticon.com/512/2405/2405479.png',
  '茶': 'https://cdn-icons-png.flaticon.com/512/2935/2935406.png',
  '咖啡': 'https://cdn-icons-png.flaticon.com/512/924/924514.png',
  '食品': 'https://cdn-icons-png.flaticon.com/512/1046/1046784.png',
  // 运动
  '跑步': 'https://cdn-icons-png.flaticon.com/512/2742/2742528.png',
  '篮球': 'https://cdn-icons-png.flaticon.com/512/484/484482.png',
  '健身': 'https://cdn-icons-png.flaticon.com/512/2936/2936886.png',
  '运动': 'https://cdn-icons-png.flaticon.com/512/2936/2936886.png',
  '户外': 'https://cdn-icons-png.flaticon.com/512/2936/2936886.png',
  // 图书
  '书': 'https://cdn-icons-png.flaticon.com/512/2232/2232688.png',
  '图书': 'https://cdn-icons-png.flaticon.com/512/2232/2232688.png',
  // 游戏
  '游戏': 'https://cdn-icons-png.flaticon.com/512/686/686589.png',
  'switch': 'https://cdn-icons-png.flaticon.com/512/686/686589.png',
  'ps5': 'https://cdn-icons-png.flaticon.com/512/686/686589.png',
  // 键盘/鼠标
  '键盘': 'https://cdn-icons-png.flaticon.com/512/2381/2381590.png',
  '鼠标': 'https://cdn-icons-png.flaticon.com/512/2381/2381573.png',
  // 分类兜底
  '手机数码': 'https://cdn-icons-png.flaticon.com/512/0/191.png',
  '电脑办公': 'https://cdn-icons-png.flaticon.com/512/2004/2004580.png',
  '家用电器': 'https://cdn-icons-png.flaticon.com/512/2847/2847039.png',
  '服装鞋帽': 'https://cdn-icons-png.flaticon.com/512/2503/2503380.png',
  '美妆个护': 'https://cdn-icons-png.flaticon.com/512/1940/1940922.png',
  '食品饮料': 'https://cdn-icons-png.flaticon.com/512/1046/1046784.png',
  '运动户外': 'https://cdn-icons-png.flaticon.com/512/2936/2936886.png',
  '图书音像': 'https://cdn-icons-png.flaticon.com/512/2232/2232688.png',
}

const getProductImage = (picUrl, product) => {
  // 先尝试根据商品名匹配本地图片库（更可靠）
  if (product) {
    const searchText = ((product.title || '') + (product.name || '') + (product.category || '')).toLowerCase()
    for (const [keyword, imgUrl] of Object.entries(productImageMap)) {
      if (searchText.includes(keyword.toLowerCase())) {
        return imgUrl
      }
    }
  }

  // 如果有有效的 picUrl 且不是随机图片服务，用作兜底
  if (picUrl && typeof picUrl === 'string' && picUrl.startsWith('http')
      && !picUrl.includes('picsum') && !picUrl.includes('placeholder') 
      && !picUrl.includes('unsplash') && !picUrl.includes('lorem') 
      && !picUrl.includes('random') && !picUrl.includes('loremflickr')
      && !picUrl.includes('example.com') && !picUrl.includes('via.placeholder')) {
    return picUrl
  }
  
  return defaultImage
}

// 图片加载失败时的处理
const onImgError = (e) => {
  if (e && e.target) {
    e.target.src = defaultImage
  }
}

/**
 * 添加到购物车（调用后端接口，同时保存 localStorage 作为缓存）
 */
const handleAddToCart = async (item) => {
  const userId = getCurrentUserId()
  if (!userId) {
    ElMessage.warning('请先登录')
    router.push('/login')
    return
  }

  try {
    await cartService.addCart({
      userId,
      productId: item.id,
      count: 1
    })

    // 同步更新 localStorage 缓存（给浮动工具栏的徽章用）
    let cart = []
    try { cart = JSON.parse(localStorage.getItem('cart') || '[]') } catch { cart = [] }
    const existing = cart.find(cartItem => cartItem.id === item.id)
    if (existing) {
      existing.quantity = (existing.quantity || 1) + 1
    } else {
      cart.push({
        id: item.id,
        title: item.title || item.name,
        name: item.name,
        price: item.price,
        picUrl: item.picUrl,
        stock: item.stock,
        category: item.category,
        quantity: 1,
        checked: false,
      })
    }
    // 同步本地缓存
    localStorage.setItem('cart', JSON.stringify(cart))

    ElMessage.success('已加入购物车')
  } catch (error) {
    console.error('加入购物车失败:', error)
    // 后端失败时回退到 localStorage
    let cart = []
    try { cart = JSON.parse(localStorage.getItem('cart') || '[]') } catch { cart = [] }
    const existing = cart.find(cartItem => cartItem.id === item.id)
    if (existing) {
      existing.quantity = (existing.quantity || 1) + 1
    } else {
      cart.push({
        id: item.id,
        title: item.title || item.name,
        name: item.name,
        price: item.price,
        picUrl: item.picUrl,
        stock: item.stock,
        category: item.category,
        quantity: 1,
        checked: false,
      })
    }
    localStorage.setItem('cart', JSON.stringify(cart))
    ElMessage.success('已加入购物车（本地缓存）')
  }
}

/**
 * 跳转到购物车
 */
const goToCart = () => {
  router.push('/cart')
}

/**
 * 跳转到订单页
 */
const goToOrders = () => {
  router.push('/orders')
}

/**
 * 优惠券列表
 */
const couponList = ref([
  { id: 1, name: '新用户9折券', label: '9折', desc: '全场商品可用，无门槛', color: 'linear-gradient(135deg,#ff9500,#ffb347)', expireText: '2026-12-31 到期', expired: false, claimed: false },
  { id: 2, name: '新人专享券', label: '¥10', desc: '满50元可用', color: 'linear-gradient(135deg,#ff6b6b,#ff4d4f)', expireText: '2026-03-31 到期', expired: false, claimed: false },
  { id: 3, name: '满减优惠券', label: '¥30', desc: '满200元可用', color: 'linear-gradient(135deg,#537ce5,#8b5fc9)', expireText: '2026-02-28 到期', expired: false, claimed: false },
  { id: 4, name: '会员专属券', label: '¥20', desc: '满100元可用', color: 'linear-gradient(135deg,#ffb347,#ff9500)', expireText: '2026-01-31 到期', expired: true, claimed: false },
])

// 初始化优惠券领取状态
try {
  const savedCoupons = JSON.parse(localStorage.getItem('my_coupons') || '[]')
  savedCoupons.forEach(id => {
    const c = couponList.value.find(x => x.id === id)
    if (c) c.claimed = true
  })
} catch {}

const handleCouponCommand = (id) => {
  const coupon = couponList.value.find(c => c.id === id)
  if (!coupon || coupon.expired || coupon.claimed) return
  coupon.claimed = true
  // 持久化
  const saved = JSON.parse(localStorage.getItem('my_coupons') || '[]')
  if (!saved.includes(id)) saved.push(id)
  localStorage.setItem('my_coupons', JSON.stringify(saved))
  ElMessage.success(`🎉 成功领取「${coupon.name}」`)
}

/**
 * 点击轮播项跳转
 */
const goToProduct = (item) => {
  ElMessage.info(`导航至：${item.title}`)
  // 实际应用中可在此处实现商品跳转逻辑
}

/**
 * 跳转到商品详情页
 */
const goToProductDetail = (product) => {
  router.push({
    name: 'ProductDetail',
    params: { id: product.id },
    query: { product: encodeURIComponent(JSON.stringify(product)) }
  })
}

/**
 * 点击首页按钮 - 展示所有商品
 */
const goToHome = () => {
  // 清空当前商品列表
  productList.value = []
  totalProducts.value = 0
  
  // 重置分类和页码
  currentCategory.value = null
  selectedNavCategory.value = 'all'
  currentPage.value = 1
  hasMore.value = true
  loading.value = true
  showFeatured.value = false
  
  // 重新加载商品列表
  fetchProductList()
  ElMessage.success('已切换到首页，展示所有商品')
}

/**
 * 用户下拉菜单命令
 */
const handleCommand = (command) => {
  if (command === 'logout') {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    ElNotification({
      title: '再见',
      message: '已成功退出登录，欢迎再次使用云商城！',
      type: 'info',
      duration: 1800,
    })
    router.push('/login')
  } else if (command === 'profile') {
    router.push('/profile')
  } else if (command === 'settings') {
    ElMessage.info('账号设置功能开发中...')
  }
}

/**
 * 搜索商品
 */
const handleSearch = () => {
  const keyword = searchKeyword.value.trim()
  if (!keyword) {
    // 如果搜索框为空，显示全部商品
    handleSearchClear()
    return
  }

  // 在当前商品列表中搜索
  if (allProducts.value.length === 0) {
    // 如果还没有保存所有商品，先保存当前列表
    allProducts.value = [...productList.value]
  }

  const filtered = allProducts.value.filter(product => {
    const title = (product.title || product.name || '').toLowerCase()
    const category = (product.category || '').toLowerCase()
    const searchTerm = keyword.toLowerCase()
    return title.includes(searchTerm) || category.includes(searchTerm)
  })

  productList.value = filtered
  totalProducts.value = filtered.length
  
  if (filtered.length === 0) {
    ElMessage.info(`未找到包含"${keyword}"的商品`)
  } else {
    ElMessage.success(`找到 ${filtered.length} 件相关商品`)
  }
}

/**
 * 清空搜索
 */
const handleSearchClear = () => {
  searchKeyword.value = ''
  if (allProducts.value.length > 0) {
    productList.value = [...allProducts.value]
    totalProducts.value = allProducts.value.length
  }
}

/**
 * 处理导航栏分类选择
 */
const handleCategorySelect = async (categoryValue) => {
  selectedNavCategory.value = categoryValue
  
  // 清空当前商品列表
  productList.value = []
  totalProducts.value = 0
  
  // 重置页码和加载状态
  currentPage.value = 1
  hasMore.value = true
  loading.value = true
  
  // 设置当前分类
  if (categoryValue === 'all') {
    currentCategory.value = null
  } else {
    currentCategory.value = categoryValue
  }
  
  // 重新加载商品列表
  try {
    await fetchProductList()
    ElMessage.success(`已切换到${categoryValue === 'all' ? '全部分类' : '分类：' + categoryValue}`)
  } catch (error) {
    console.error('切换分类失败:', error)
    ElMessage.error('切换分类失败，请重试')
  }
}

/**
 * 获取分类列表 - 直接调用后端接口
 */
const fetchCategories = async () => {
  try {
    // 直接使用axios调用后端接口
    const response = await productAPI.get('/product/categories')
    const categoriesData = response.data
    
    console.log('后端返回的分类数据:', categoriesData)
    
    // 处理返回的字符串数组
    if (Array.isArray(categoriesData)) {
      navCategories.value = categoriesData.map((categoryName, index) => ({
        id: index + 1,
        name: categoryName,
        value: categoryName, // 直接使用中文分类名作为value
        count: 0
      }))
      console.log('格式化后的分类列表:', navCategories.value)
    } else {
      console.error('分类数据格式错误:', categoriesData)
      ElMessage.error('分类数据格式错误')
    }
  } catch (error) {
    console.error('获取分类列表失败:', error)
    console.error('错误详情:', error.response?.data || error.message)
    
    // 尝试使用productService.getCategories()作为后备
    try {
      const response = await productService.getCategories()
      const responseData = response.data
      
      let categoriesData = []
      if (Array.isArray(responseData)) {
        categoriesData = responseData
      } else if (responseData && typeof responseData === 'object') {
        categoriesData = responseData.data || responseData.list || []
      }
      
      if (categoriesData.length > 0) {
        navCategories.value = categoriesData.map((cat, index) => ({
          id: cat.id || index + 1,
          name: cat.name || cat.categoryName || `分类${index + 1}`,
          value: cat.value || cat.code || cat.name || `category${index + 1}`,
          count: cat.count || 0
        }))
        console.log('通过productService获取的分类列表:', navCategories.value)
      } else {
        throw new Error('分类数据为空')
      }
    } catch (fallbackError) {
      console.error('后备方案也失败:', fallbackError)
      // 使用默认分类数据
      navCategories.value = [
        { id: 1, name: '手机数码', value: '手机数码', count: 0 },
        { id: 2, name: '电脑办公', value: '电脑办公', count: 0 },
        { id: 3, name: '家用电器', value: '家用电器', count: 0 },
        { id: 4, name: '服装鞋帽', value: '服装鞋帽', count: 0 },
        { id: 5, name: '美妆个护', value: '美妆个护', count: 0 },
        { id: 6, name: '食品饮料', value: '食品饮料', count: 0 },
        { id: 7, name: '运动户外', value: '运动户外', count: 0 },
        { id: 8, name: '图书音像', value: '图书音像', count: 0 }
      ]
      ElMessage.warning('使用默认分类数据，请检查后端服务')
    }
  }
}

/**
 * 页面加载
 */
onMounted(async () => {
  searchKeyword.value = ''
  allProducts.value = []
  productList.value = []
  totalProducts.value = 0
  showFeatured.value = false
  currentCategory.value = null
  selectedNavCategory.value = 'all'
  currentPage.value = 1
  hasMore.value = true

  // 读取用户信息
  const userStr = localStorage.getItem('user')
  if (userStr) {
    try {
      const user = JSON.parse(userStr)
      userNickname.value = user.nickname || user.username || '用户'
    } catch (e) {
      console.error('解析用户信息失败:', e)
    }
  }

  // 获取分类列表
  await fetchCategories()
  
  // 获取商品列表
  await fetchProductList()

  // 初始化粒子动画
  initParticles()
})

onUnmounted(() => {
  if (particleAnimId) cancelAnimationFrame(particleAnimId)
})

function initParticles() {
  const canvas = particleCanvas.value
  if (!canvas) return
  const ctx = canvas.getContext('2d')
  let w = canvas.width = window.innerWidth
  let h = canvas.height = window.innerHeight

  const PARTICLE_COUNT = 30
  const particles = []
  const colors = [
    'rgba(255,255,255,0.25)',
    'rgba(163,163,255,0.35)',
    'rgba(255,255,255,0.18)',
    'rgba(163,163,255,0.22)',
    'rgba(192,132,252,0.20)'
  ]

  for (let i = 0; i < PARTICLE_COUNT; i++) {
    particles.push({
      x: Math.random() * w,
      y: Math.random() * h,
      r: Math.random() * 6 + 3,
      dx: (Math.random() - 0.5) * 0.25,
      dy: (Math.random() - 0.5) * 0.25,
      color: colors[Math.floor(Math.random() * colors.length)]
    })
  }

  function draw() {
    ctx.clearRect(0, 0, w, h)
    for (const p of particles) {
      ctx.beginPath()
      ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2)
      ctx.fillStyle = p.color
      ctx.fill()
      p.x += p.dx
      p.y += p.dy
      if (p.x < -10) p.x = w + 10
      if (p.x > w + 10) p.x = -10
      if (p.y < -10) p.y = h + 10
      if (p.y > h + 10) p.y = -10
    }
    particleAnimId = requestAnimationFrame(draw)
  }
  draw()

  window.addEventListener('resize', () => {
    w = canvas.width = window.innerWidth
    h = canvas.height = window.innerHeight
  })
}
</script>

<style scoped>
/* 全局变量 */
:root {
  --primary-color: #537ce5;
  --primary-dark: #232a4d;
  --primary-light: #4b2067;
  --accent-color: #a3a3ff;
  --success-color: #7fff7f;
  --warning-color: #ffb347;
  --danger-color: #ff7f7f;
}

/* 粒子画布 */
.particle-canvas {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;
  pointer-events: none;
}

/* 图片加载失败占位 */
.image-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #2a2a45 0%, #35284d 100%);
  color: rgba(255,255,255,0.35);
  font-size: 0.8rem;
  gap: 6px;
}

.home-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #232a4d 0%, #4b2067 100%);
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  position: relative;
}

/* ========== 导航栏 ========== */
.nav-bar {
  background: rgba(35, 42, 77, 0.65);
  box-shadow: 0 2px 16px rgba(0, 0, 0, 0.25);
  position: sticky;
  top: 0;
  z-index: 1000;
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.nav-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 12px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.logo {
  font-size: 22px;
  font-weight: bold;
  margin-right: 40px;
  min-width: 120px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.logo-icon {
  font-size: 28px;
  background: linear-gradient(135deg, #a3a3ff 0%, #c084fc 50%, #f0abfc 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  filter: drop-shadow(0 0 6px rgba(163, 163, 255, 0.5));
}

.nav-menu {
  flex: 1;
}

.nav-menu-inner {
  background: transparent !important;
  border-bottom: none !important;
}

.nav-menu-inner :deep(.el-menu-item) {
  color: rgba(255, 255, 255, 0.85) !important;
  background: transparent !important;
  font-weight: 500;
}

.nav-menu-inner :deep(.el-menu-item:hover) {
  background: rgba(255, 255, 255, 0.08) !important;
  color: #fff !important;
}

/* 搜索栏样式 */
.search-bar {
  margin: 0 20px;
  min-width: 240px;
  max-width: 300px;
}

.search-input {
  --el-input-bg-color: rgba(255, 255, 255, 0.08);
  --el-input-border-color: rgba(255, 255, 255, 0.15);
  --el-input-hover-border-color: rgba(163, 163, 255, 0.5);
  --el-input-focus-border-color: #a3a3ff;
  --el-input-text-color: rgba(255, 255, 255, 0.9);
  --el-input-placeholder-color: rgba(255, 255, 255, 0.5);
}

.search-input :deep(.el-input__wrapper) {
  background-color: rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s;
}

.search-input :deep(.el-input__wrapper:hover) {
  background-color: rgba(255, 255, 255, 0.12);
  box-shadow: 0 2px 12px rgba(163, 163, 255, 0.2);
}

.search-input :deep(.el-input__wrapper.is-focus) {
  background-color: rgba(255, 255, 255, 0.15);
  box-shadow: 0 0 0 2px rgba(163, 163, 255, 0.3);
}

.search-icon {
  cursor: pointer;
  color: rgba(255, 255, 255, 0.6);
  transition: all 0.3s;
  font-size: 16px;
}

.search-icon:hover {
  color: #a3a3ff;
  transform: scale(1.1);
}

.user-info {
  margin-left: auto;
  color: rgba(255, 255, 255, 0.85);
  font-size: 14px;
}

.user-info :deep(.el-dropdown-link) {
  color: rgba(255, 255, 255, 0.85);
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-info :deep(.el-dropdown-link:hover) {
  color: #a3a3ff;
}

/* 分类下拉菜单样式 */
.nav-menu-inner :deep(.el-dropdown-link) {
  color: rgba(255, 255, 255, 0.85) !important;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
}

.nav-menu-inner :deep(.el-dropdown-link:hover) {
  color: #a3a3ff !important;
}

/* 分类下拉菜单 - 磨砂玻璃效果 */
.category-dropdown {
  background: rgba(255, 255, 255, 0.85) !important;
  backdrop-filter: blur(16px) saturate(180%) !important;
  -webkit-backdrop-filter: blur(16px) saturate(180%) !important;
  border: 1px solid rgba(255, 255, 255, 0.25) !important;
  box-shadow: 0 8px 32px 0 rgba(31, 38, 135, 0.1) !important;
  border-radius: 12px !important;
  padding: 8px 0 !important;
  margin-top: 8px !important;
  max-height: 400px !important;
  overflow-y: auto !important;
}

.category-dropdown :deep(.el-dropdown-menu__item) {
  color: #232a4d !important;
  font-size: 0.95rem !important;
  padding: 10px 20px !important;
  transition: all 0.2s ease !important;
}

.category-dropdown :deep(.el-dropdown-menu__item:hover) {
  background: rgba(83, 124, 229, 0.1) !important;
  color: #537ce5 !important;
}

.category-dropdown :deep(.el-dropdown-menu__item.selected) {
  background: linear-gradient(135deg, #537ce5 0%, #8b5fc9 100%) !important;
  color: white !important;
  font-weight: 600 !important;
}

.category-dropdown :deep(.el-dropdown-menu__item.selected:hover) {
  background: linear-gradient(135deg, #537ce5 0%, #8b5fc9 100%) !important;
  opacity: 0.9 !important;
}

/* 导航栏选中状态高亮 */
.nav-menu-inner :deep(.el-menu-item.is-active .el-dropdown-link) {
  color: #a3a3ff !important;
  font-weight: 600 !important;
}

.nav-menu-inner :deep(.el-menu-item.is-active .el-dropdown-link .el-icon) {
  color: #a3a3ff !important;
}

.glow-text {
  color: #fff;
  font-size: 22px;
  font-weight: 800;
  letter-spacing: 1px;
  text-shadow: 0 0 8px rgba(163,163,255,0.6), 0 0 16px rgba(75,32,103,0.4);
}

/* ========== 主内容 ========== */
.main-content {
  flex: 1;
  max-width: 1100px;
  margin: 0 auto;
  width: 100%;
  padding: 6px 4px;
  box-sizing: border-box;
  position: relative;
  z-index: 1;
}

/* ========== 分类卡片 ========== */
.category-section {
  margin-bottom: 30px;
  position: relative;
  z-index: 1;
}

.category-card {
  position: relative;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 20px 16px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 2px solid rgba(255, 255, 255, 0.08);
  overflow: hidden;
  height: 140px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.category-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.3);
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.15);
}

.category-card-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  opacity: 0.3;
  z-index: 1;
}

.category-icon {
  margin-bottom: 12px;
}

.category-name {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 6px;
}

.category-count {
  font-size: 0.85rem;
  opacity: 0.7;
  color: rgba(255,255,255,0.45);
}

/* ========== 英雄区 ========== */
.banner {
  background: linear-gradient(135deg, #537ce5 0%, #8b5fc9 100%);
  border: none;
  border-radius: 12px;
  padding: 20px 40px;
  color: #fff;
  margin-bottom: 30px;
  box-shadow: 0 8px 32px #53000030;
  text-align: center;
  height: auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.banner-content h1 {
  font-size: 2.2rem;
  margin-bottom: 18px;
  margin-top: 0;
  font-weight: bold;
}

.banner-content p {
  font-size: 1.1rem;
  margin-bottom: 32px;
  opacity: 0.9;
}

/* ========== 商品区 ========== */
.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.total-count {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.45);
  font-weight: 500;
}

/* ========== 商品卡片 (Glassmorphism) ========== */
.glassmorphism {
  background: rgba(40, 40, 60, 0.35);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(16px) saturate(180%);
  -webkit-backdrop-filter: blur(16px) saturate(180%);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.product-card-wrapper {
  padding: 0;
  height: 100%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.product-card-wrapper:hover {
  transform: translateY(-12px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.35);
  background: rgba(50, 50, 75, 0.45);
  border-color: rgba(255, 255, 255, 0.15);
}

/* 商品图片 */
.product-image {
  position: relative;
  width: 100%;
  height: 220px;
  overflow: hidden;
  background: linear-gradient(135deg, #2a2a45 0%, #35284d 100%);
  border-radius: 16px 16px 0 0;
}

.product-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.product-badge {
  position: absolute;
  top: 12px;
  right: 12px;
  background: rgba(255, 127, 127, 0.85);
  color: white;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: bold;
  backdrop-filter: blur(8px);
}

/* 商品信息 */
.product-info {
  padding: 16px 14px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.product-title {
  font-size: 1.05rem;
  font-weight: bold;
  color: rgba(255, 255, 255, 0.9);
  margin: 0;
  line-height: 1.4;
  text-overflow: ellipsis;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

/* 价格和库存 */
.product-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
}

.price {
  font-size: 1.3rem;
  color: #ff6b6b;
  font-weight: bold;
}

.stock {
  font-size: 0.9rem;
  color: #7fff7f;
  font-weight: 600;
}

.stock.low-stock {
  color: #ff7f7f;
  animation: blink 1.5s ease-in-out infinite;
}

@keyframes blink {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.6; }
}

/* 分类标签 */
.product-category {
  margin: 4px 0;
}

.product-category :deep(.el-tag) {
  background: rgba(163, 163, 255, 0.15) !important;
  color: #c4b5fd !important;
  border-color: rgba(163, 163, 255, 0.25) !important;
}

/* 加入购物车按钮 */
.btn-add-cart {
  margin-top: auto;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #537ce5 0%, #8b5fc9 100%);
  color: white;
  font-size: 0.95rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.btn-add-cart:hover {
  transform: scale(1.05);
  box-shadow: 0 6px 16px rgba(83, 124, 229, 0.4);
}

.btn-add-cart:active {
  transform: scale(0.98);
}

/* ========== 无限滚动容器 ========== */
.infinite-scroll-container {
  min-height: 400px;
  position: relative;
}

/* ========== 加载更多状态 ========== */
.loading-more {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: rgba(255, 255, 255, 0.45);
  font-size: 0.95rem;
  gap: 12px;
}

.loading-more .loading-icon {
  font-size: 24px;
  color: #537ce5;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

/* ========== 没有更多数据 ========== */
.no-more-data {
  text-align: center;
  padding: 30px 20px;
  color: rgba(255, 255, 255, 0.3);
  font-size: 0.9rem;
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  margin-top: 20px;
}

/* ========== 空状态 ========== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 50px 20px;
  text-align: center;
  color: rgba(255, 255, 255, 0.4);
}

.empty-state p {
  margin-top: 16px;
  font-size: 1.1rem;
}

/* ========== 底部 ========== */
.footer {
  background: rgba(15, 12, 30, 0.6);
  border-top: 1px solid rgba(255, 255, 255, 0.06);
  color: #fff;
  margin-top: auto;
  padding: 30px 20px 15px;
  position: relative;
  z-index: 1;
}

.footer-content {
  max-width: 1400px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
  margin-bottom: 25px;
}

.footer-section h4 {
  margin-bottom: 16px;
  font-size: 1.1rem;
}

.footer-section p {
  font-size: 0.9rem;
  opacity: 0.8;
  margin-bottom: 8px;
}

.footer-section ul {
  list-style: none;
  padding: 0;
}

.footer-section li {
  margin-bottom: 8px;
}

.footer-section a {
  color: #a3a3ff;
  text-decoration: none;
  transition: color 0.3s;
}

.footer-section a:hover {
  color: #fff;
}

.footer-bottom {
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  padding-top: 20px;
  text-align: center;
  font-size: 0.85rem;
  opacity: 0.7;
}

/* ========== 响应式设计 ========== */
@media (max-width: 768px) {
  .nav-content {
    flex-wrap: wrap;
    gap: 12px;
  }

  .nav-menu {
    width: 100%;
  }

  .search-bar {
    width: 100%;
    min-width: auto;
    max-width: none;
    margin: 0;
    order: 3;
  }

  .user-info {
    width: 100%;
    margin-left: 0;
  }

  .banner {
    padding: 20px;
    height: auto;
  }

  .banner-content h1 {
    font-size: 1.8rem;
  }

  .banner-content p {
    font-size: 1rem;
  }

  .main-content {
    padding: 15px 12px;
  }

  .category-section,
  .products-section {
    margin-bottom: 30px;
  }

  .section-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .product-card-wrapper {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }

  .product-card-wrapper:hover {
    transform: translateY(-8px);
  }
}

@media (max-width: 480px) {
  .banner {
    height: auto;
    padding: 16px;
    margin-bottom: 25px;
  }

  .banner-content h1 {
    font-size: 1.4rem;
  }

  .banner-content p {
    font-size: 0.9rem;
  }

  .product-title {
    font-size: 1rem;
  }

  .price {
    font-size: 1.2rem;
  }
}

.stock {
  color: rgba(255,255,255,0.45) !important;
  font-weight: 500;
}

.low-stock {
  color: #ff4d4f !important;
  font-weight: bold;
}

/* ========== 分类精选展示区域 ========== */
.featured-section {
  margin-bottom: 20px;
  padding: 20px;
  border-radius: 18px;
  border: 1.5px solid;
  position: relative;
  z-index: 1;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
}

.featured-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

.featured-title {
  display: flex;
  align-items: center;
  gap: 8px;
}

.featured-title h2 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 800;
}

.featured-icon {
  font-size: 1.1rem;
}

.featured-actions {
  display: flex;
  gap: 8px;
}

.featured-refresh {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 16px;
  border: 1.5px solid;
  border-radius: 20px;
  background: rgba(255,255,255,0.08);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s;
  color: rgba(255,255,255,0.7);
}

.featured-refresh:hover {
  opacity: 0.85;
  transform: scale(1.05);
}

.featured-close {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 6px 16px;
  border: 1.5px solid rgba(255,255,255,0.1);
  border-radius: 20px;
  background: rgba(255,255,255,0.05);
  color: rgba(255,255,255,0.4);
  font-size: 0.82rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.25s;
}

.featured-close:hover {
  background: rgba(255,255,255,0.1);
  transform: scale(1.05);
}

.featured-card {
  border: 2px solid !important;
  position: relative;
  overflow: hidden;
  transition: all 0.3s;
}

.featured-card:hover {
  transform: translateY(-6px) !important;
}

.featured-badge {
  position: absolute;
  top: 8px;
  left: -28px;
  padding: 2px 32px;
  color: #fff;
  font-size: 0.65rem;
  font-weight: 700;
  transform: rotate(-45deg);
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  letter-spacing: 1px;
}

.featured-cart-btn {
  color: #fff !important;
  border: none !important;
}

.featured-cart-btn:hover {
  opacity: 0.9;
  box-shadow: 0 4px 14px rgba(0,0,0,0.15) !important;
}
</style>

<!-- 优惠券下拉菜单样式（需非scoped，因为dropdown teleport到body） -->
<style>
.coupon-dropdown-menu {
  padding: 8px 0 0 !important;
  min-width: 340px !important;
}

.coupon-dropdown-menu .el-dropdown-menu__item {
  padding: 0 !important;
  line-height: normal !important;
  height: auto !important;
}

.coupon-dropdown-menu .el-dropdown-menu__item:hover {
  background: transparent !important;
}

.coupon-dropdown-item {
  display: flex;
  align-items: stretch;
  gap: 0;
  padding: 6px 12px;
  border-radius: 10px;
  margin: 0 8px 8px;
  border: 1.5px solid rgba(0,0,0,0.06);
  transition: all 0.2s;
  background: #fff;
  min-height: 66px;
}

.coupon-dropdown-item:hover:not(.expired) {
  border-color: rgba(83,124,229,0.2);
  box-shadow: 0 2px 12px rgba(83,124,229,0.08);
  transform: translateY(-1px);
}

.coupon-dropdown-item.expired {
  opacity: 0.5;
}

.coupon-dd-left {
  width: 56px;
  min-width: 56px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
}

.coupon-dd-val {
  color: #fff;
  font-size: 1rem;
  font-weight: 800;
  text-shadow: 0 1px 4px rgba(0,0,0,0.1);
}

.coupon-dd-right {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 2px;
  min-width: 0;
}

.coupon-dd-name {
  font-size: 0.88rem;
  font-weight: 600;
  color: #232a4d;
}

.coupon-dd-desc {
  font-size: 0.72rem;
  color: #888;
}

.coupon-dd-expire {
  font-size: 0.68rem;
  color: #bbb;
}

.coupon-dd-action {
  display: flex;
  align-items: center;
  margin-left: 8px;
}

.dd-tag {
  padding: 3px 12px;
  border-radius: 12px;
  font-size: 0.72rem;
  font-weight: 600;
  white-space: nowrap;
}

.claim-tag {
  background: linear-gradient(135deg, #ff9500, #ffb347);
  color: #fff;
  cursor: pointer;
}

.claimed-tag {
  background: #e8f8f0;
  color: #43b581;
}

.expired-tag {
  background: #f5f5f5;
  color: #ccc;
}

.coupon-dd-footer {
  text-align: center;
  padding: 8px 0 10px;
  font-size: 0.75rem;
  color: #bbb;
  border-top: 1px solid rgba(0,0,0,0.04);
}
</style>
